import React, { useRef } from "react";
import { Input, Textarea } from "./_shared";
import { api, resolveUrl, formatApiError } from "../../../lib/api";

const DEFAULT_USPS = [
  { title_ro: "Asigurare completa", title_en: "Comprehensive insurance", desc_ro: "RCA + CASCO incluse in fiecare rezervare. Pachete optionale cu franșiza zero.", desc_en: "Full RCA + CASCO coverage included in every booking, with optional zero-deductible packages." },
  { title_ro: "Doar flota premium", title_en: "Premium-only fleet", desc_ro: "Inginerie germana si rafinament italian: Audi, BMW, Porsche, Mercedes — toate din 2022 sau mai noi.", desc_en: "Hand-picked German engineering and Italian flair: Audi, BMW, Porsche, Mercedes — all 2022 or newer." },
  { title_ro: "Flexibil si rapid", title_en: "Flexible & fast", desc_ro: "Rezervari online in mai putin de 2 minute. Livrare gratuita in Pascani. Check-in express.", desc_en: "Online booking in under 2 minutes. Free delivery within Pascani. Express check-in." },
  { title_ro: "Pret transparent", title_en: "Honest pricing", desc_ro: "Toate taxele, kilometrii si asigurarea inclusi. Fara taxe ascunse, garantat.", desc_en: "All taxes, mileage and insurance included. No hidden fees, ever." },
  { title_ro: "Local si de incredere", title_en: "Local & trusted", desc_ro: "Afacere de familie cu sediul in Pascani, servim regiunea Iasi pentru mobilitate premium.", desc_en: "Family business based in Pascani, serving the Iasi region for premium mobility needs." },
  { title_ro: "Stare impecabila", title_en: "Pristine condition", desc_ro: "Fiecare masina este detaliata inainte de fiecare rezervare. Calitate de showroom.", desc_en: "Every vehicle is detailed before each booking. Showroom-quality interior and exterior." },
];

function Section({ title, children }) {
  return (
    <div className="card-surface p-6 space-y-4">
      <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{title}</div>
      {children}
    </div>
  );
}

function BilingualRow({ labelRo, labelEn, valueRo, valueEn, onChange, rows }) {
  const Comp = rows ? Textarea : Input;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Comp label={labelRo} value={valueRo} onChange={(v) => onChange("ro", v)} rows={rows} />
      <Comp label={labelEn} value={valueEn} onChange={(v) => onChange("en", v)} rows={rows} />
    </div>
  );
}

function ImageUpload({ label, slot, currentUrl, onUploaded }) {
  const ref = useRef();
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/settings/about-image?slot=${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data.url);
    } catch (err) {
      alert(formatApiError(err));
    }
    e.target.value = "";
  };

  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="flex items-center gap-4">
        {currentUrl ? (
          <img
            src={resolveUrl(currentUrl)}
            alt={label}
            className="h-24 w-36 object-cover"
            style={{ border: "1px solid var(--brand-border)" }}
          />
        ) : (
          <div
            className="h-24 w-36 flex items-center justify-center text-xs"
            style={{ border: "1px dashed var(--brand-border)", color: "var(--brand-text-secondary)" }}
          >
            Nicio imagine
          </div>
        )}
        <div>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} />
          <button type="button" className="btn-ghost text-xs" onClick={() => ref.current?.click()}>
            {currentUrl ? "Schimba" : "Incarca imagine"}
          </button>
          <p className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>PNG, JPG, WEBP</p>
        </div>
      </div>
    </div>
  );
}

export default function AboutTab({ settings, setSettings, saveAbout }) {
  const about = settings.about || {};

  const set = (key, val) => setSettings({ ...settings, about: { ...about, [key]: val } });

  const usps = about.usps || DEFAULT_USPS;
  const setUsp = (i, field, val) => {
    const next = usps.map((u, idx) => idx === i ? { ...u, [field]: val } : u);
    set("usps", next);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="about-tab">

      <Section title="Hero">
        <BilingualRow
          labelRo="Titlu (RO)" labelEn="Titlu (EN)"
          valueRo={about.hero_title_ro ?? "Mai mult decat o inchiriere — o relatie"}
          valueEn={about.hero_title_en ?? "More than a rental — a partnership"}
          onChange={(lang, v) => set(lang === "ro" ? "hero_title_ro" : "hero_title_en", v)}
        />
        <BilingualRow
          labelRo="Subtitlu (RO)" labelEn="Subtitlu (EN)"
          valueRo={about.hero_subtitle_ro ?? "Masini premium, servicii de familie. Descopera ce ne face cea mai de incredere inchiriere auto din Pascani."}
          valueEn={about.hero_subtitle_en ?? "Premium cars, family service. Discover what makes us the most trusted car rental in Pascani."}
          onChange={(lang, v) => set(lang === "ro" ? "hero_subtitle_ro" : "hero_subtitle_en", v)}
          rows={2}
        />
      </Section>

      <Section title="Povestea noastra">
        <BilingualRow
          labelRo="Eyebrow (RO)" labelEn="Eyebrow (EN)"
          valueRo={about.story_eyebrow_ro ?? "Povestea noastra"}
          valueEn={about.story_eyebrow_en ?? "Our story"}
          onChange={(lang, v) => set(lang === "ro" ? "story_eyebrow_ro" : "story_eyebrow_en", v)}
        />
        <BilingualRow
          labelRo="Titlu (RO)" labelEn="Titlu (EN)"
          valueRo={about.story_title_ro ?? "Nascut din pasiunea pentru masini fine"}
          valueEn={about.story_title_en ?? "Born from a passion for fine automobiles"}
          onChange={(lang, v) => set(lang === "ro" ? "story_title_ro" : "story_title_en", v)}
        />
        <BilingualRow
          labelRo="Paragraf 1 (RO)" labelEn="Paragraf 1 (EN)"
          valueRo={about.story_p1_ro ?? ""}
          valueEn={about.story_p1_en ?? ""}
          onChange={(lang, v) => set(lang === "ro" ? "story_p1_ro" : "story_p1_en", v)}
          rows={3}
        />
        <BilingualRow
          labelRo="Paragraf 2 (RO)" labelEn="Paragraf 2 (EN)"
          valueRo={about.story_p2_ro ?? ""}
          valueEn={about.story_p2_en ?? ""}
          onChange={(lang, v) => set(lang === "ro" ? "story_p2_ro" : "story_p2_en", v)}
          rows={3}
        />
        <BilingualRow
          labelRo="Paragraf 3 (RO)" labelEn="Paragraf 3 (EN)"
          valueRo={about.story_p3_ro ?? ""}
          valueEn={about.story_p3_en ?? ""}
          onChange={(lang, v) => set(lang === "ro" ? "story_p3_ro" : "story_p3_en", v)}
          rows={3}
        />
      </Section>

      <Section title="Citat (sectiunea vizuala)">
        <BilingualRow
          labelRo="Citat (RO)" labelEn="Citat (EN)"
          valueRo={about.quote_ro ?? "Nu vindem inchirieri — cream experiente."}
          valueEn={about.quote_en ?? "We don't sell rentals — we craft experiences."}
          onChange={(lang, v) => set(lang === "ro" ? "quote_ro" : "quote_en", v)}
          rows={2}
        />
      </Section>

      <Section title="Avantaje (6 carduri)">
        {usps.map((u, i) => (
          <div key={i} className="border-b pb-4 last:border-0 last:pb-0" style={{ borderColor: "var(--brand-border)" }}>
            <div className="label-eyebrow mb-3" style={{ opacity: 0.55 }}>#{i + 1}</div>
            <div className="grid sm:grid-cols-2 gap-4 mb-3">
              <Input label="Titlu (RO)" value={u.title_ro || ""} onChange={(v) => setUsp(i, "title_ro", v)} />
              <Input label="Titlu (EN)" value={u.title_en || ""} onChange={(v) => setUsp(i, "title_en", v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Textarea label="Descriere (RO)" value={u.desc_ro || ""} onChange={(v) => setUsp(i, "desc_ro", v)} rows={2} />
              <Textarea label="Descriere (EN)" value={u.desc_en || ""} onChange={(v) => setUsp(i, "desc_en", v)} rows={2} />
            </div>
          </div>
        ))}
      </Section>

      <Section title="Call to action (final)">
        <BilingualRow
          labelRo="Eyebrow (RO)" labelEn="Eyebrow (EN)"
          valueRo={about.cta_eyebrow_ro ?? "Gata cand esti tu"}
          valueEn={about.cta_eyebrow_en ?? "Ready when you are"}
          onChange={(lang, v) => set(lang === "ro" ? "cta_eyebrow_ro" : "cta_eyebrow_en", v)}
        />
        <BilingualRow
          labelRo="Titlu (RO)" labelEn="Titlu (EN)"
          valueRo={about.cta_title_ro ?? "Hai sa-ti planificam urmatoarea calatorie"}
          valueEn={about.cta_title_en ?? "Let's plan your next drive"}
          onChange={(lang, v) => set(lang === "ro" ? "cta_title_ro" : "cta_title_en", v)}
        />
        <BilingualRow
          labelRo="Text (RO)" labelEn="Text (EN)"
          valueRo={about.cta_text_ro ?? "Exploreaza colectia sau cere o oferta personalizata — revenim in maxim 30 minute."}
          valueEn={about.cta_text_en ?? "Browse our collection or request a personalised quote — we'll get back to you within 30 minutes."}
          onChange={(lang, v) => set(lang === "ro" ? "cta_text_ro" : "cta_text_en", v)}
          rows={2}
        />
      </Section>

      <Section title="Imagini">
        <ImageUpload
          label="Hero (fundal pagina)"
          slot="hero"
          currentUrl={about.hero_img}
          onUploaded={(url) => set("hero_img", url)}
        />
        <ImageUpload
          label="Povestea noastra (coloana stanga)"
          slot="story"
          currentUrl={about.story_img}
          onUploaded={(url) => set("story_img", url)}
        />
        <ImageUpload
          label="Showroom (sectiunea vizuala lata)"
          slot="showroom"
          currentUrl={about.showroom_img}
          onUploaded={(url) => set("showroom_img", url)}
        />
        <p className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>
          Imaginile sunt incarcate imediat dupa selectare si salvate automat.
        </p>
      </Section>

      <button className="btn-gold-filled" onClick={saveAbout} data-testid="about-tab-save">
        Salveaza pagina Despre
      </button>
    </div>
  );
}
